import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, MapPin, FileText, Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Tabs } from '@/components/Tabs';
import { extractErrorMessage } from '@/lib/api';
import { useLegalEntities } from '@/hooks/useVehicles';
import type { Site, SiteInput, SiteStatus, SiteType, SiteContractEmbed } from '@/lib/types';
import { formatSar } from '@/lib/trip-math';

interface SiteFormProps {
  initial?: Site | null;
  onSubmit: (input: SiteInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  /** When viewing existing site, show its contracts list */
  contracts?: SiteContractEmbed[];
}

const STATUSES: SiteStatus[] = ['active', 'on_hold', 'completed', 'cancelled'];
const SITE_TYPES: SiteType[] = ['construction', 'industrial', 'camp', 'port', 'commercial', 'residential', 'other'];

const blank: SiteInput = {
  nameEn: '', nameAr: '', siteType: 'construction',
  city: '', region: '', address: '',
  latitude: null, longitude: null,
  startDate: '', expectedEndDate: '', actualEndDate: '',
  legalEntityId: null,
  status: 'active',
  notes: '',
};

export function SiteForm({ initial, onSubmit, onCancel, submitLabel, contracts }: SiteFormProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';
  const { data: legalEntities = [] } = useLegalEntities();

  const [form, setForm] = useState<SiteInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        nameEn: initial.nameEn,
        nameAr: initial.nameAr ?? '',
        siteType: initial.siteType,
        city: initial.city ?? '',
        region: initial.region ?? '',
        address: initial.address ?? '',
        latitude: initial.latitude != null ? Number(initial.latitude) : null,
        longitude: initial.longitude != null ? Number(initial.longitude) : null,
        startDate: initial.startDate ? initial.startDate.slice(0, 10) : '',
        expectedEndDate: initial.expectedEndDate ? initial.expectedEndDate.slice(0, 10) : '',
        actualEndDate: initial.actualEndDate ? initial.actualEndDate.slice(0, 10) : '',
        legalEntityId: initial.legalEntityId,
        status: initial.status,
        notes: initial.notes ?? '',
      });
    } else {
      const defaultLE = legalEntities.find((le) => le.isOperational) ?? legalEntities[0];
      setForm({ ...blank, legalEntityId: defaultLE?.id ?? null });
    }
  }, [initial, legalEntities]);

  const update = <K extends keyof SiteInput>(key: K, value: SiteInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.nameEn?.trim()) { setError(t('sites.errors.nameRequired')); return; }

    const payload: SiteInput = {
      nameEn: form.nameEn.trim(),
      siteType: form.siteType,
      status: form.status,
    };
    if (form.nameAr?.trim()) payload.nameAr = form.nameAr.trim();
    if (form.city?.trim()) payload.city = form.city.trim();
    if (form.region?.trim()) payload.region = form.region.trim();
    if (form.address?.trim()) payload.address = form.address.trim();
    if (form.latitude != null) payload.latitude = form.latitude;
    if (form.longitude != null) payload.longitude = form.longitude;
    if (form.startDate?.trim()) payload.startDate = form.startDate.trim();
    if (form.expectedEndDate?.trim()) payload.expectedEndDate = form.expectedEndDate.trim();
    if (form.actualEndDate?.trim()) payload.actualEndDate = form.actualEndDate.trim();
    if (form.legalEntityId) payload.legalEntityId = form.legalEntityId;
    if (form.notes?.trim()) payload.notes = form.notes.trim();

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const InfoTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <Field label={t('sites.field.nameEn')} required>
        <Input value={form.nameEn ?? ''} onChange={(e) => update('nameEn', e.target.value)} placeholder="Amaala, Sharma..." />
      </Field>
      <Field label={t('sites.field.nameAr')}>
        <Input dir="rtl" value={form.nameAr ?? ''} onChange={(e) => update('nameAr', e.target.value)} placeholder="أمالا، شرما..." />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t('sites.field.siteType')} required>
          <Select value={form.siteType ?? 'construction'} onChange={(v) => update('siteType', v as SiteType)}>
            {SITE_TYPES.map((st) => <option key={st} value={st}>{t(`sites.siteType.${st}`)}</option>)}
          </Select>
        </Field>
        <Field label={t('sites.field.legalEntity')}>
          <Select value={form.legalEntityId ?? ''} onChange={(v) => update('legalEntityId', v || null)}>
            <option value="">{t('sites.placeholder.noEntity')}</option>
            {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.code} — {le.nameEn}</option>)}
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">{t('sites.help.legalEntity')}</p>
        </Field>
      </div>
      <Field label={t('sites.field.status')}>
        <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as SiteStatus)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`sites.status.${s}`)}</option>)}
        </Select>
      </Field>
    </div>
  );

  const LocationTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t('sites.field.city')}>
          <Input value={form.city ?? ''} onChange={(e) => update('city', e.target.value)} placeholder="Tabuk, Riyadh..." />
        </Field>
        <Field label={t('sites.field.region')}>
          <Input value={form.region ?? ''} onChange={(e) => update('region', e.target.value)} placeholder="Tabuk Province..." />
        </Field>
      </div>
      <Field label={t('sites.field.address')}>
        <Input value={form.address ?? ''} onChange={(e) => update('address', e.target.value)} />
      </Field>
      <fieldset className="space-y-3 pt-3 border-t border-border">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t('sites.section.coordinates')}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('sites.field.latitude')}>
            <Input type="number" step="0.0000001" min={-90} max={90} dir="ltr"
              value={form.latitude ?? ''}
              onChange={(e) => update('latitude', e.target.value ? Number(e.target.value) : null)}
              className="font-mono" placeholder="27.5114" />
          </Field>
          <Field label={t('sites.field.longitude')}>
            <Input type="number" step="0.0000001" min={-180} max={180} dir="ltr"
              value={form.longitude ?? ''}
              onChange={(e) => update('longitude', e.target.value ? Number(e.target.value) : null)}
              className="font-mono" placeholder="35.7244" />
          </Field>
        </div>
        <p className="text-[11px] text-muted-foreground">{t('sites.help.coordinates')}</p>
      </fieldset>
    </div>
  );

  const TimelineTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label={t('sites.field.startDate')}>
          <Input type="date" value={form.startDate ?? ''} onChange={(e) => update('startDate', e.target.value)} />
        </Field>
        <Field label={t('sites.field.expectedEndDate')}>
          <Input type="date" value={form.expectedEndDate ?? ''} onChange={(e) => update('expectedEndDate', e.target.value)} />
        </Field>
        <Field label={t('sites.field.actualEndDate')}>
          <Input type="date" value={form.actualEndDate ?? ''} onChange={(e) => update('actualEndDate', e.target.value)} />
          <p className="text-[11px] text-muted-foreground mt-1">{t('sites.help.actualEnd')}</p>
        </Field>
      </div>
      <Field label={t('sites.field.notes')}>
        <textarea value={form.notes ?? ''} onChange={(e) => update('notes', e.target.value)} rows={4}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      </Field>

      {contracts && contracts.length > 0 && (
        <fieldset className="space-y-2 pt-4 border-t border-border">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('sites.section.activeContracts', { count: contracts.length })}
          </legend>
          {contracts.map((c) => (
            <div key={c.id} className="rounded-md border border-border bg-muted/10 p-3 flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {c.client.nameEn} · <span className="text-muted-foreground">{t(`contracts.tripType.${c.tripType}`)}</span>
                </p>
                <p className="text-[11px] text-muted-foreground font-mono">
                  {c.code} · {formatSar(c.clientRateAtTarget)} {t('contracts.preview.perTarget', { qty: c.targetQty })}
                </p>
              </div>
              <ChevronRight className={`h-4 w-4 text-muted-foreground ${isRtl ? 'rotate-180' : ''}`} />
            </div>
          ))}
        </fieldset>
      )}
    </div>
  );

  const tabs = [
    { key: 'info',     label: <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{t('sites.tab.info')}</span>, content: InfoTab },
    { key: 'location', label: <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{t('sites.tab.location')}</span>, content: LocationTab },
    { key: 'timeline', label: <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{t('sites.tab.timeline')}</span>, content: TimelineTab },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <Tabs tabs={tabs} defaultKey="info" />
      <div className="shrink-0 border-t border-border bg-muted/30">
        {error && (
          <p className="text-sm text-destructive p-3 mx-6 my-3 rounded bg-destructive/10 border border-destructive/30 flex items-start gap-2" role="alert">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>{t('common.cancel')}</Button>
          )}
          <Button type="submit" variant="gold" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel ?? t('common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {children}
    </select>
  );
}
